import React, { useEffect, useMemo, useRef, useState } from "react";
import { ClientEvent, MatrixEventEvent, RoomEvent } from "matrix-js-sdk";
import { ChatContext } from "src/scenes/Chat/ChatContext";
import { useChatCalls, useGetChats, useMatrixClient, usePresenceListener } from "src/scenes/Chat/hooks";
import { FEATURE_FLAGS, hasFeature } from "src/constants/FeatureFlag";
import { SubscriptionMode, UserKeys } from "src/constants/userDetails";
import ChatCallPanel from "src/scenes/Chat/components/ChatCallPanel";
import {
    MATRIX_RTC_CALL_NOTIFY,
    MATRIX_RTC_MEMBER,
    MATRIX_RTC_NOTIFICATION
} from "src/constants/matrixEventTypes";
import { getUnreadCountWithoutCallNotes } from "src/scenes/Chat/utils/unreadNotifications";
import { useCallRingtone } from "src/scenes/Chat/hooks/useCallRingtone";

export function canStartMatrixSession(user) {
    const hasEligibleSubscription = user?.[UserKeys.SUBSCRIPTION_MODE] === SubscriptionMode.DEMO ||
        user?.[UserKeys.SUBSCRIPTION_IS_VALID] === true;

    return Boolean(user?.[UserKeys.TOKEN]) &&
        hasFeature(user, FEATURE_FLAGS.BUSINESS_CHAT) &&
        hasEligibleSubscription;
}

function MatrixSessionProvider({ children, loginPassphrase, onLoginPassphraseConsumed, updateUserPresenceState, user }) {
    const token = user?.[UserKeys.TOKEN];
    const matrixIsEnabled = canStartMatrixSession(user);
    const headers = useMemo(() => ({
        headers: { Authorization: "Bearer " + token }
    }), [token]);
    const callEventRefreshTimeoutRef = useRef(null);
    const initialChatListTokenRef = useRef(null);

    useEffect(() => {
        if (!matrixIsEnabled && loginPassphrase && onLoginPassphraseConsumed) {
            onLoginPassphraseConsumed();
        }
    }, [loginPassphrase, matrixIsEnabled, onLoginPassphraseConsumed]);

    const {
        matrixClient,
        isInitialSyncComplete,
        matrixRecoveryRequired,
        runListener
    } = useMatrixClient(headers, {
        enabled: matrixIsEnabled,
        loginPassphrase,
        onLoginPassphraseConsumed,
    });

    const {
        onlineUsersMatrixId,
        handleSetOwnUserPresence
    } = usePresenceListener(matrixClient, isInitialSyncComplete, headers, user, updateUserPresenceState);

    const [leadershipRoles, setLeadershipRoles] = useState([]);
    const {
        activeChatListDetails,
        chatList,
        isFetchingActiveChats,
        populateActiveChatListDetails,
        setActiveChatListDetails,
        setChatList
    } = useGetChats(matrixClient, setLeadershipRoles);
    const siteWideChatList = useMemo(() => {
        if (!Array.isArray(activeChatListDetails)) {
            return [];
        }

        const chats = activeChatListDetails.flatMap(chatList => chatList?.data?.chats || []);
        return Array.from(new Map(chats.map(chat => [chat.matrixRoomId, chat])).values());
    }, [activeChatListDetails]);
    const {
        callState,
        callNotes,
        activeGroupCall,
        isSendingCallAction,
        isSendingCallNote,
        localStream,
        localScreenShareStream,
        remoteStream,
        remoteStreams,
        callChat,
        callLobby,
        callWindowRef,
        screenShareIsActive,
        rememberKnownChats,
        startCall,
        openCallLobby,
        closeCallLobby,
        focusCallWindow,
        startCallFromLobby,
        setLocalAudioEnabled,
        setLocalMicrophoneDevice,
        setLocalVideoEnabled,
        setLocalCameraDevice,
        setLocalSpeakerDevice,
        toggleScreenShare,
        setLocalBackgroundEffect,
        addCallNote,
        answerCall,
        answerCallFromLobby,
        joinGroupCall,
        hangupCall,
        rejectCall
    } = useChatCalls(matrixClient, headers, null, siteWideChatList, null, user);

    useCallRingtone(callState?.status, callState?.suppressRingtone === true);

    useEffect(() => {
        if (!token || initialChatListTokenRef.current === token) {
            return;
        }

        initialChatListTokenRef.current = token;
        populateActiveChatListDetails();
    }, [token]);

    useEffect(() => {
        if (!matrixClient || !isInitialSyncComplete || !matrixIsEnabled) {
            return;
        }

        populateActiveChatListDetails(true);
    }, [isInitialSyncComplete, matrixClient, matrixIsEnabled]);

    useEffect(() => {
        if (!matrixClient || !isInitialSyncComplete || !matrixIsEnabled) {
            return undefined;
        }

        const refreshChatsForCallEvent = (event) => {
            if ([
                MATRIX_RTC_CALL_NOTIFY,
                MATRIX_RTC_NOTIFICATION,
                MATRIX_RTC_MEMBER
            ].includes(event?.getType?.())) {
                if (callEventRefreshTimeoutRef.current) {
                    return;
                }
                callEventRefreshTimeoutRef.current = window.setTimeout(() => {
                    callEventRefreshTimeoutRef.current = null;
                    populateActiveChatListDetails(true);
                }, 200);
            }
        };

        matrixClient.on("Room.timeline", refreshChatsForCallEvent);
        matrixClient.on(MatrixEventEvent.Decrypted, refreshChatsForCallEvent);
        return () => {
            window.clearTimeout(callEventRefreshTimeoutRef.current);
            callEventRefreshTimeoutRef.current = null;
            matrixClient.off("Room.timeline", refreshChatsForCallEvent);
            matrixClient.off(MatrixEventEvent.Decrypted, refreshChatsForCallEvent);
        };
    }, [isInitialSyncComplete, matrixClient, matrixIsEnabled]);

    const matrixUnreadCount = useMatrixUnreadCount(matrixClient, isInitialSyncComplete, siteWideChatList);
    const currentMatrixUserId = matrixClient?.getUserId?.() || user?.[UserKeys.MATRIX_USER_ID];
    const contextValue = useMemo(() => ({
        headers,
        currentMatrixUserId,
        matrixClient,
        matrixIsEnabled,
        isInitialSyncComplete,
        matrixRecoveryRequired,
        matrixUnreadCount,
        runListener,
        chatCollections: {
            activeChatListDetails,
            chatList,
            isFetchingActiveChats,
            leadershipRoles,
            populateActiveChatListDetails,
            setActiveChatListDetails,
            setChatList
        },
        useChatCalls: {
            callState,
            callNotes,
            activeGroupCall,
            isSendingCallAction,
            isSendingCallNote,
            localStream,
            localScreenShareStream,
            remoteStream,
            remoteStreams,
            callChat,
            callLobby,
            callWindowRef,
            screenShareIsActive,
            rememberKnownChats,
            startCall,
            openCallLobby,
            closeCallLobby,
            focusCallWindow,
            startCallFromLobby,
            setLocalAudioEnabled,
            setLocalMicrophoneDevice,
            setLocalVideoEnabled,
            setLocalCameraDevice,
            setLocalSpeakerDevice,
            toggleScreenShare,
            setLocalBackgroundEffect,
            addCallNote,
            answerCall,
            answerCallFromLobby,
            joinGroupCall,
            hangupCall,
            rejectCall
        },
        usePresenceListener: {
            onlineUsersMatrixId,
            handleSetOwnUserPresence
        }
    }), [
        headers,
        currentMatrixUserId,
        handleSetOwnUserPresence,
        isInitialSyncComplete,
        matrixRecoveryRequired,
        activeGroupCall,
        activeChatListDetails,
        addCallNote,
        answerCall,
        answerCallFromLobby,
        callChat,
        callNotes,
        callLobby,
        callWindowRef,
        chatList,
        closeCallLobby,
        focusCallWindow,
        callState,
        hangupCall,
        isSendingCallAction,
        isSendingCallNote,
        isFetchingActiveChats,
        joinGroupCall,
        leadershipRoles,
        localStream,
        localScreenShareStream,
        matrixClient,
        matrixIsEnabled,
        matrixUnreadCount,
        rememberKnownChats,
        openCallLobby,
        onlineUsersMatrixId,
        rejectCall,
        populateActiveChatListDetails,
        remoteStream,
        remoteStreams,
        runListener,
        screenShareIsActive,
        setLocalBackgroundEffect,
        setLocalAudioEnabled,
        setLocalMicrophoneDevice,
        setLocalVideoEnabled,
        setLocalCameraDevice,
        setLocalSpeakerDevice,
        setActiveChatListDetails,
        setChatList,
        toggleScreenShare,
        startCallFromLobby,
        startCall
    ]);

    return (
        <ChatContext.Provider value={contextValue}>
            {children}
            {(!callWindowRef.current || callWindowRef.current.closed) && <ChatCallPanel
                callState={callState}
                callNotes={callNotes}
                isSendingCallNote={isSendingCallNote}
                onAddCallNote={addCallNote}
                currentChat={callChat}
                currentUser={user}
                matrixClient={matrixClient}
                currentMatrixUserId={currentMatrixUserId}
                callLobby={callLobby}
                callWindowRef={callWindowRef}
                isSendingCallAction={isSendingCallAction}
                screenShareIsActive={screenShareIsActive}
                localStream={localStream}
                localScreenShareStream={localScreenShareStream}
                remoteStreams={remoteStreams}
                onCancelLobby={closeCallLobby}
                onStartFromLobby={startCallFromLobby}
                onFocusWindow={focusCallWindow}
                onSetLocalAudioEnabled={setLocalAudioEnabled}
                onSetLocalMicrophoneDevice={setLocalMicrophoneDevice}
                onSetLocalVideoEnabled={setLocalVideoEnabled}
                onSetLocalCameraDevice={setLocalCameraDevice}
                onSetLocalSpeakerDevice={setLocalSpeakerDevice}
                onToggleScreenShare={toggleScreenShare}
                onSetLocalBackgroundEffect={setLocalBackgroundEffect}
                onAnswerFromLobby={answerCallFromLobby}
                onHangup={hangupCall}
                onReject={rejectCall}
            />}
        </ChatContext.Provider>
    );
}

function useMatrixUnreadCount(matrixClient, isInitialSyncComplete, siteWideChatList) {
    const [matrixUnreadCount, setMatrixUnreadCount] = useState(0);
    const chatRoomIds = useMemo(
        () => new Set(siteWideChatList.map(chat => chat.matrixRoomId).filter(Boolean)),
        [siteWideChatList]
    );

    useEffect(() => {
        if (!matrixClient || !isInitialSyncComplete) {
            setMatrixUnreadCount(0);
            return;
        }

        const updateUnreadCount = () => {
            setMatrixUnreadCount(calculateMatrixUnreadCount(matrixClient, chatRoomIds));
        };

        updateUnreadCount();

        const eventNames = [
            ClientEvent.Sync,
            ClientEvent.Event,
            ClientEvent.Room,
            ClientEvent.DeleteRoom,
            RoomEvent.Timeline,
            RoomEvent.Receipt,
            RoomEvent.LocalEchoUpdated,
            RoomEvent.MyMembership
        ];

        eventNames.forEach(eventName => matrixClient.on(eventName, updateUnreadCount));

        return () => {
            eventNames.forEach(eventName => matrixClient.off(eventName, updateUnreadCount));
        };
    }, [chatRoomIds, matrixClient, isInitialSyncComplete]);

    return matrixUnreadCount;
}

function calculateMatrixUnreadCount(matrixClient, chatRoomIds) {
    const rooms = typeof matrixClient?.getRooms === "function"
        ? matrixClient.getRooms()
        : Object.values(matrixClient?.store?.rooms || {});

    return rooms.reduce((total, room) => {
        if (!chatRoomIds.has(room.roomId)) {
            return total;
        }

        if (room?.getMyMembership?.() && room.getMyMembership() !== "join") {
            return total;
        }

        const roomUnreadCount = getUnreadCountWithoutCallNotes(
            room,
            matrixClient.getUserId?.() || ""
        );
        return total + roomUnreadCount;
    }, 0);
}

export default MatrixSessionProvider;
